<script setup lang='ts'>

    import ViewBaseAccessPolicy from '@/apps/config/AccessPolicy/ViewBaseAccessPolicy.vue';
    import { computed, onMounted, ref, Ref } from 'vue';
    import { PolicyRole, PolicySystem } from '@/shared/helpers/api.ts';
    import { useAccessPolicyStore } from '@/stores/accessPolicyStore.ts';
    import ModalCreateSystem from '@/apps/config/AccessPolicy/ModalCreateSystem.vue';
    import ModalCreateRole from '@/apps/config/AccessPolicy/ModalCreateRole.vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import useShow from '@/shared/composables/useShow';

    type Cols = {
        field: string;
        label: string;
        width?: string;
    };

    const roleCols: Cols[] = [
        {
            field: 'name',
            label: 'Role Name',
            width: '100'
        },
        {
            field: 'externalName',
            label: 'External Name',
            width: '100'
        },
        {
            field: 'description',
            label: 'Description'
        }
    ];

    const store = useAccessPolicyStore();
    const { showError } = useShow();

    const checkedRoles: Ref<PolicyRole[]> = ref([]);

    const showCreateModal: Ref<boolean> = ref(false);
    const showEditModal: Ref<boolean> = ref(false);
    const showDeleteModal: Ref<boolean> = ref(false);
    const roleCreation: Ref<boolean> = ref(false);

    const editPolicySystem: Ref<PolicySystem | undefined> = ref(undefined);

    const loading = computed(() => {
        return store.policySystems?.length !== 0;
    });

    const systemRoles = computed(() => {
        if (store.selectedSystem === undefined) {
            return [];
        }

        return store.selectedSystem?.roles;
    });

    const enabledRoleDeletion = computed(() => {
        return checkedRoles.value.length > 0;
    });

    onMounted(async () => {
        await reloadSystems();
    });

    async function reloadSystems(): Promise<void> {
        try {
            await store.loadSystems();
        } catch {
            showError('Unable to load policy systems');
        }
    }

    async function onClickDeleteRoles() {
        const tempSelectedSystem = store.selectedSystem!.name;

        try {
            const storeActions: Promise<void>[] = checkedRoles.value.map(async (role) => {
                await store.deletePolicyRole(role);
            });
            await Promise.all(storeActions);
        } catch (_) {
            showError('Unexpected error occurred while trying to delete roles.');
        } finally {
            checkedRoles.value = [];
            store.clearSelection();
            await reloadSystems();
            store.setSelectedSystem(tempSelectedSystem);
        }
    }

    async function onClickDeleteSystemConfirmed() {
        await store.deletePolicySystem();
        await reloadSystems();
        store.clearSelection();
        showDeleteModal.value = !showDeleteModal.value;
    }

    async function onClickSaveSystem(system: PolicySystem) {
        try {
            await store.savePolicySystem(system);
        } catch (_) {
            showError('Unable to save a new system');
        } finally {
            onCancel();
            await reloadSystems();
            store.setSelectedSystem(system.name);
        }
    }

    async function onClickSaveRoles(role: PolicyRole) {
        try {
            await store.savePolicyRole(role);
            await reloadSystems();
        } catch (_) {
            showError('Unable to save a new role');
        } finally {
            const selectedSystem = store.selectedSystem;
            onCancel();

            if (selectedSystem !== undefined) {
                store.setSelectedSystem(selectedSystem.name);
            }
        }
    }

    async function onClickUpdateSystem() {
        await store.updatePolicySystem(editPolicySystem.value!);
        await reloadSystems();
        store.setSelectedSystem(editPolicySystem.value!.name);
        showEditModal.value = !showEditModal.value;
    }

    function onClickEditSystem() {
        editPolicySystem.value = {
            name: store.selectedSystem!.name || '',
            description: store.selectedSystem?.description || '',
            roles: []
        };
        showEditModal.value = !showEditModal.value;
    }

    function onClickShowModal(roles: boolean) {
        if (roles) {
            roleCreation.value = true;
        }
        showCreateModal.value = !showCreateModal.value;
    }

    function onCancel() {
        if (roleCreation.value) {
            roleCreation.value = false;
        }
        showCreateModal.value = !showCreateModal.value;
        store.clearSelection();
    }

    function onCancelEditModal() {
        showEditModal.value = !showEditModal.value;
    }

    function onCancelDeleteModal() {
        showDeleteModal.value = !showDeleteModal.value;
    }

</script>

<template>
    <view-base-access-policy>
        <template #controls>
            <div class="level">
                <div class="level-left mt-2 mb-3">
                    <b-button
                        class="is-info"
                        @click="onClickShowModal(false)">
                        <span class="icon is-small">
                            <i class="mdi mdi-plus" />
                        </span>
                        <span>Add System</span>
                    </b-button>

                    <b-button
                        class="is-info ml-2"
                        @click="onClickShowModal(true)">
                        <span class="icon is-small">
                            <i class="mdi mdi-plus" />
                        </span>
                        <span>Add new Role</span>
                    </b-button>
                </div>
            </div>
        </template>
        <template
            v-if="showCreateModal"
            #modal>
            <Component
                :is="ModalCreateRole"
                v-if="roleCreation"
                @onCancel="onCancel"
                @onSave="onClickSaveRoles" />
            <Component
                :is="ModalCreateSystem"
                v-if="!roleCreation"
                @onCancel="onCancel"
                @onSave="onClickSaveSystem" />
        </template>
        <template
            v-else-if="showDeleteModal"
            #modal>
            <modal-base @onCancel="onCancelDeleteModal">
                <template #modal-header>
                    Delete System
                </template>
                <template #modal-body>
                    Are you sure you want to delete this System: <strong>{{ store.selectedSystem?.name }}</strong>?
                </template>
                <template #modal-footer>
                    <b-field
                        grouped
                        position="right">
                        <b-button
                            label="Cancel"
                            @click="onCancelDeleteModal" />
                        <b-button
                            label="Delete"
                            type="is-primary"
                            @click="onClickDeleteSystemConfirmed" />
                    </b-field>
                </template>
            </modal-base>
        </template>
        <template
            v-else-if="showEditModal"
            #modal>
            <modal-base @onCancel="onCancelEditModal">
                <template #modal-header>
                    Edit System
                </template>
                <template #modal-body>
                    <b-field label="Name">
                        <b-input
                            :value="store.selectedSystem!.name"
                            disabled />
                    </b-field>
                    <b-field
                        label="Description">
                        <b-input
                            v-model="editPolicySystem!.description"
                            label="Description"
                            placeholder="Description" />
                    </b-field>
                </template>
                <template #modal-footer>
                    <b-field
                        grouped
                        position="right">
                        <b-button
                            label="Cancel"
                            @click="onCancelEditModal" />
                        <b-button
                            label="Save"
                            type="is-primary"
                            @click="onClickUpdateSystem" />
                    </b-field>
                </template>
            </modal-base>
        </template>
        <template #systemandroles>
            <main class="columns">
                <section class="column is-half">
                    <div class="column-header">
                        <h2 class="title is-3 ml-2">
                            Systems
                        </h2>
                    </div>
                    <section>
                        <b-loading
                            v-if="!loading"
                            :loading="!loading" />
                        <b-table
                            v-else
                            v-model:selected="store.selectedSystem"
                            :data="store.policySystems">
                            <b-table-column
                                v-slot="props"
                                label="System Name">
                                {{ props.row.name }}
                            </b-table-column>
                            <b-table-column
                                v-slot="props"
                                label="Description">
                                {{ props.row.description }}
                            </b-table-column>
                        </b-table>
                    </section>
                    <section>
                        <div class="level">
                            <div class="level-left" />
                            <div class="level-right mt-2 mb-3">
                                <b-button
                                    class="is-info"
                                    :disabled="store.selectedSystem === undefined"
                                    @click="onClickEditSystem">
                                    Edit
                                </b-button>
                                <b-button
                                    :disabled="store.selectedSystem === undefined"
                                    class="is-danger ml-2"
                                    @click="showDeleteModal = !showDeleteModal">
                                    Delete
                                </b-button>
                            </div>
                        </div>
                    </section>
                </section>
                <aside class="column">
                    <header>
                        <h2 class="title is-3 ml-2">
                            Roles
                        </h2>
                    </header>
                    <section>
                        <b-table
                            v-model:checked-rows="checkedRoles"
                            :data="systemRoles"
                            :columns="roleCols"
                            checkbox-position="right"
                            checkable />
                        <div
                            v-if="systemRoles?.length > 0"
                            class="level">
                            <div class="level-left" />
                            <div class="level-right">
                                <b-button
                                    :disabled="!enabledRoleDeletion"
                                    class="is-danger mt-2 mb-3 "
                                    @click="onClickDeleteRoles">
                                    <span v-if="checkedRoles.length > 1">
                                        Delete All
                                    </span>
                                    <span v-else>
                                        Delete
                                    </span>
                                </b-button>
                            </div>
                        </div>
                    </section>
                </aside>
            </main>
        </template>
    </view-base-access-policy>
</template>

<style scoped>

</style>
